import { useEffect, useMemo, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';

import type { EventoDoAtendimento, MensagemConversa } from '@/api/secretario';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchHighlight } from '@/components/ui/search-highlight';
import { SearchInput } from '@/components/ui/search-input';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';

import { quando } from './util';

const FUSO = 'America/Sao_Paulo';

function dia(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: FUSO });
}

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: FUSO });
}

function plural(n: number): string {
  return `${n} ${n === 1 ? 'mensagem' : 'mensagens'}`;
}

/**
 * A conversa fica fora do dossiê: uma linha com o tamanho dela e "Abrir conversa".
 * Aberta, ocupa um painel lateral (tela cheia no celular) com rolagem própria e
 * busca. Dentro da página, uma conversa de centenas de mensagens virava uma
 * rolagem enorme entre o caso e o resto.
 */
export function Conversa({ mensagens, primeiroNome }: { mensagens: MensagemConversa[]; primeiroNome: string }) {
  const [aberta, setAberta] = useState(false);
  const comTexto = useMemo(() => mensagens.filter((m) => (m.conteudo ?? '').trim() !== ''), [mensagens]);
  if (comTexto.length === 0) return null;
  const resumo = `${plural(comTexto.length)}, de ${quando(comTexto[0].created_at)} a ${quando(comTexto[comTexto.length - 1].created_at)}`;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0 space-y-0.5">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conversa no WhatsApp</h2>
          <p className="text-sm text-muted-foreground">{resumo}</p>
        </div>
        <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" onClick={() => setAberta(true)}>
          <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
          Abrir conversa
        </Button>
      </CardContent>
      <ConversaAberta open={aberta} onOpenChange={setAberta} mensagens={comTexto} primeiroNome={primeiroNome} resumo={resumo} />
    </Card>
  );
}

interface ConversaAbertaProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mensagens: MensagemConversa[];
  primeiroNome: string;
  resumo: string;
}

/**
 * O balão do CLIENTE em destaque (a fonte primária), o do Secretário apagado.
 * A busca filtra as mensagens com o termo; "Ver no contexto" volta à conversa
 * inteira rolando até a mensagem escolhida.
 */
function ConversaAberta({ open, onOpenChange, mensagens, primeiroNome, resumo }: ConversaAbertaProps) {
  const [busca, setBusca] = useState('');
  const [foco, setFoco] = useState<string | null>(null);
  const termo = busca.trim();
  const visiveis = useMemo(() => {
    const q = termo.toLowerCase();
    return q ? mensagens.filter((m) => (m.conteudo ?? '').toLowerCase().includes(q)) : mensagens;
  }, [mensagens, termo]);

  useEffect(() => {
    if (!foco || termo) return;
    document.getElementById(`mensagem-${foco}`)?.scrollIntoView?.({ block: 'center' });
  }, [foco, termo]);

  const mudarAbertura = (aberta: boolean) => {
    onOpenChange(aberta);
    if (!aberta) {
      setBusca('');
      setFoco(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={mudarAbertura}>
      <SheetContent
        side="right"
        className="top-[var(--env-banner-h,0px)] flex h-[calc(100dvh-var(--env-banner-h,0px))] w-full flex-col gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="space-y-3 border-b px-4 py-3 text-left">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <SheetTitle>Conversa no WhatsApp</SheetTitle>
              <SheetDescription>{resumo}</SheetDescription>
            </div>
            <SheetClose asChild>
              <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label="Fechar a conversa">
                <X className="h-5 w-5" aria-hidden="true" />
              </Button>
            </SheetClose>
          </div>
          <SearchInput
            value={busca}
            onChange={(valor) => {
              // Busca nova esquece a mensagem em foco: limpar a busca depois não pode rolar de volta a ela.
              setBusca(valor);
              setFoco(null);
            }}
            placeholder="Buscar na conversa"
          />
          {termo && (
            <p role="status" className="text-xs text-muted-foreground">
              {visiveis.length === 0 ? 'Nenhuma mensagem com esse termo.' : `${plural(visiveis.length)} com esse termo.`}
            </p>
          )}
        </SheetHeader>

        <ol className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-4 py-3" aria-label="Mensagens">
          {visiveis.map((m, i) => {
            const cliente = m.direcao === 'ENTRADA';
            const novoDia = !termo && (i === 0 || dia(m.created_at) !== dia(visiveis[i - 1].created_at));
            const emFoco = !termo && foco === m.id;
            return (
              <li key={m.id} className="contents">
                {novoDia && (
                  <span className="self-center py-1 text-[11px] uppercase tracking-wide text-muted-foreground">{dia(m.created_at)}</span>
                )}
                <div
                  id={`mensagem-${m.id}`}
                  className={`max-w-[88%] whitespace-pre-wrap rounded-2xl border px-3 py-2 text-sm ${
                    cliente ? 'self-end rounded-br-sm bg-card' : 'self-start rounded-bl-sm bg-muted text-muted-foreground'
                  } ${emFoco ? 'ring-2 ring-primary' : ''}`}
                >
                  <SearchHighlight text={m.conteudo ?? ''} query={termo} />
                  <time className="mt-1 block text-[11px] opacity-70 tabular-nums" dateTime={m.created_at}>
                    {termo ? `${dia(m.created_at)} ` : ''}
                    {hora(m.created_at)} · {cliente ? primeiroNome : 'Secretário'}
                  </time>
                  {termo && (
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto min-h-11 justify-start p-0 text-xs sm:min-h-0"
                      onClick={() => {
                        setFoco(m.id);
                        setBusca('');
                      }}
                    >
                      Ver no contexto
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </SheetContent>
    </Sheet>
  );
}

/** Sempre à vista: é curta e diz em que pé o atendimento está sem precisar abrir nada. */
export function LinhaDoTempo({ eventos }: { eventos: EventoDoAtendimento[] }) {
  if (eventos.length === 0) return null;
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Linha do tempo</h2>
        <ol className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          {eventos.map((e, i) => (
            <li key={`${e.em}-${i}`} className="contents">
              <time className="text-xs text-muted-foreground tabular-nums" dateTime={e.em}>
                {quando(e.em)}
              </time>
              <span>{e.evento}</span>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
